import type {
  ScoringWeights,
  RelevanceConfig,
  TrendTier,
  PenaltySettings,
  KeywordSalesTier,
  HeroRankTier,
  SampleKeywordPreset,
  FactorKey,
} from "./types";

export const ALIAS_META: Record<string, { section: string; field: string }> = {
  kps_search_score_max: { section: "Scoring Weights", field: "Search Volume Weight" },
  kps_hero_rank_score_max: { section: "Scoring Weights", field: "Hero Rank Weight" },
  kps_hero_relevance_score_max: { section: "Scoring Weights", field: "Relevance Score Weight" },
  kps_search_trend_score_max: { section: "Scoring Weights", field: "Search Trend Weight" },
  kps_asin_density_score_max: { section: "ASIN Density Scoring", field: "Scoring Weight" },
  kps_keyword_sales_score_max: { section: "Keyword Sales Scoring", field: "Scoring Weight" },
  kps_competition_score_max: { section: "Competition Scoring", field: "Scoring Weight" },
  kps_relevance_enabled: { section: "Relevance Scoring", field: "Factor Enabled" },
  kps_search_volume_enabled: { section: "Search Volume Scoring", field: "Factor Enabled" },
  kps_hero_rank_enabled: { section: "Hero Rank Scoring", field: "Factor Enabled" },
  kps_asin_density_enabled: { section: "ASIN Density Scoring", field: "Factor Enabled" },
  kps_keyword_sales_enabled: { section: "Keyword Sales Scoring", field: "Factor Enabled" },
  kps_search_trend_enabled: { section: "Search Trend Scoring", field: "Factor Enabled" },
  kps_competition_enabled: { section: "Competition Scoring", field: "Factor Enabled" },
  kps_keyword_sales_tier1_points: { section: "Keyword Sales Scoring", field: "Tier 1 Points (Under $5,000)" },
  kps_keyword_sales_tier2_points: { section: "Keyword Sales Scoring", field: "Tier 2 Points ($5,000–$8,000)" },
  kps_keyword_sales_tier3_points: { section: "Keyword Sales Scoring", field: "Tier 3 Points ($8,000–$12,000)" },
  kps_keyword_sales_tier4_points: { section: "Keyword Sales Scoring", field: "Tier 4 Points ($12,000–$25,000)" },
  kps_keyword_sales_tier5_points: { section: "Keyword Sales Scoring", field: "Tier 5 Points ($25,000+)" },
  kps_hero_rank_tier1_points: { section: "Hero Rank Scoring", field: "Not Ranked Points" },
  kps_hero_rank_tier2_points: { section: "Hero Rank Scoring", field: "Top 10 Points" },
  kps_hero_rank_tier3_points: { section: "Hero Rank Scoring", field: "Close to Top Points (#11–#30)" },
  kps_hero_rank_tier4_points: { section: "Hero Rank Scoring", field: "Page 1 Points (#31–#100)" },
  kps_hero_rank_tier5_points: { section: "Hero Rank Scoring", field: "Beyond Page 1 Points (#101+)" },
  kps_search_trend_score_tier1_max_bound: { section: "Search Trend Scoring", field: "Tier 1 Max Bound" },
  kps_search_trend_score_tier2_max_bound: { section: "Search Trend Scoring", field: "Tier 2 Max Bound" },
  kps_search_trend_score_tier3_max_bound: { section: "Search Trend Scoring", field: "Tier 3 Max Bound" },
  kps_search_trend_score_tier4_max_bound: { section: "Search Trend Scoring", field: "Tier 4 Max Bound" },
  kps_search_trend_score_tier5_max_bound: { section: "Search Trend Scoring", field: "Tier 5 Max Bound" },
  kps_search_trend_score_tier1_points: { section: "Search Trend Scoring", field: "Tier 1 Points" },
  kps_search_trend_score_tier2_points: { section: "Search Trend Scoring", field: "Tier 2 Points" },
  kps_search_trend_score_tier3_points: { section: "Search Trend Scoring", field: "Tier 3 Points" },
  kps_search_trend_score_tier4_points: { section: "Search Trend Scoring", field: "Tier 4 Points" },
  kps_search_trend_score_tier5_points: { section: "Search Trend Scoring", field: "Tier 5 Points" },
  hero_relevance_competitor_brand_penalty_multiplier: { section: "Penalty Flags", field: "Competitor Brand Penalty" },
  hero_relevance_no_overlap_penalty_multiplier: { section: "Penalty Flags", field: "No Lexical Overlap Penalty" },
  hero_embeddings_components: { section: "Relevance Scoring", field: "Hero Embedding Components" },
  kps_hero_relevance_penalty_bound: { section: "Relevance Scoring", field: "Penalty Bound" },
  kps_hero_relevance_penalty_multiplier: { section: "Relevance Scoring", field: "Penalty Multiplier" },
  kps_hero_relevance_boost_bound: { section: "Relevance Scoring", field: "Boost Bound" },
  kps_hero_relevance_boost_multiplier: { section: "Relevance Scoring", field: "Boost Multiplier" },
  landscape_min_search_volume: { section: "Landscape Generation", field: "Minimum Search Volume" },
  landscape_min_ranked_competitors: { section: "Landscape Generation", field: "Minimum Ranked Competitors" },
  // AUT-430: saved from AI Configuration, not KPS. No ALIAS_META section of its own exists for
  // AI-configuration aliases, so this keeps the fallback "Other" grouping the change log renders
  // it under today (formatAliasFallback), while giving it the renamed field label.
  seo_strategy_gap_threshold: { section: "Other", field: "Strategy Gap Threshold" },
  // AUT-245: Brand Intelligence configuration (section 7).
  bi_asp_band_pct: { section: "Brand Intelligence", field: "ASP Band Width (%)" },
  bi_growth_threshold_pp: { section: "Brand Intelligence", field: "Growth Status Threshold (pp)" },
  bi_large_gap_pp: { section: "Brand Intelligence", field: "Large SOV Gap Threshold (pp)" },
  bi_trend_deadband_pp: { section: "Brand Intelligence", field: "Trend Dead-band (pp)" },
  bi_hero_concentration_pct: { section: "Brand Intelligence", field: "Hero ASIN Concentration (%)" },
  bi_health_weight_sov: { section: "Brand Intelligence", field: "Health Weight — SOV" },
  bi_health_weight_revshare: { section: "Brand Intelligence", field: "Health Weight — Revenue Share" },
  bi_health_weight_growth: { section: "Brand Intelligence", field: "Health Weight — Growth" },
  bi_full_catalog_bsr_threshold: { section: "Brand Intelligence", field: "Full Catalog BSR Threshold" },
  bi_refresh_cooldown_hours: { section: "Brand Intelligence", field: "Refresh Cooldown (hours)" },
};

/**
 * AUT-362: KPS Configuration and Brand Intelligence share the one configuration-setting change
 * log, so each page must filter it to its own settings instead of showing both. Brand
 * Intelligence owns exactly ALIAS_META's "Brand Intelligence" section, so an allow-list is right
 * here. KPS Configuration must NOT mirror it as an allow-list: ALIAS_META is not the full alias
 * universe (aliases saved from other tabs -- e.g. AI Configuration's `theme_*` /
 * `seo_strategy_gap_threshold` -- have no ALIAS_META entry), and an allow-list would drop their
 * change rows from every dialog. The KPS tab therefore shows the COMPLEMENT (everything not Brand
 * Intelligence), which keeps the log lossless and renders unlabeled aliases under "Other" as
 * before -- see KPSConfigurationTab.
 */
export const BRAND_INTELLIGENCE_ALIASES: ReadonlySet<string> = new Set(
  Object.keys(ALIAS_META).filter((alias) => ALIAS_META[alias].section === "Brand Intelligence"),
);

export const defaultWeights: ScoringWeights = {
  searchVolume: 15,
  heroRank: 15,
  relevance: 37.5,
  searchVolumeTrend: 7.5,
};

export const defaultRelevance: RelevanceConfig = {
  heroComponents: ["Title", "Brand", "Bullets", "Features", "Description"],
};

export const defaultTrendTiers: TrendTier[] = [
  { label: "Tier 1", maxBound: 5, points: 0 },
  { label: "Tier 2", maxBound: 100, points: 1.5 },
  { label: "Tier 3", maxBound: 150, points: 3 },
  { label: "Tier 4", maxBound: 250, points: 5 },
  { label: "Tier 5", maxBound: 999, points: 7.5 },
];

export const defaultPenalties: PenaltySettings = {
  competitorBrandPenalty: 0.5,
  noOverlapPenalty: 0.75,
};

export const defaultAsinDensityMax = 15;
export const defaultKeywordSalesMax = 10;
export const defaultCompetitionMax = 15;

export const defaultFactorEnabled: Record<FactorKey, boolean> = {
  relevance: true,
  searchVolume: true,
  // AUT-232: Hero Rank is off by default. The other six enabled weights
  // (37.5 + 15 + 15 + 10 + 15 + 7.5) already total 100, so including Hero
  // Rank's 15 would push the default to an unbalanced 115.
  heroRank: false,
  asinDensity: true,
  keywordSales: true,
  searchTrend: true,
  competition: true,
};

export const defaultKeywordSalesTiers: KeywordSalesTier[] = [
  { label: "Under $5,000", points: 1 },
  { label: "$5,000 – $8,000", points: 3 },
  { label: "$8,000 – $12,000", points: 5, highlighted: true },
  { label: "$12,000 – $25,000", points: 8, highlighted: true, sweetSpot: true },
  { label: "$25,000+", points: 10, highlighted: true },
];

export const defaultHeroRankTiers: HeroRankTier[] = [
  { condition: "Not ranked", range: "null", points: 0, nullRange: true },
  { condition: "Top 10", range: "#1 – #10", points: 0 },
  { condition: "Close to top", range: "#11 – #30", points: 15, sweetSpot: true },
  { condition: "Page 1", range: "#31 – #100", points: 12 },
  { condition: "Beyond page 1", range: "#101+", points: 10 },
];

export const sampleKeywordPresets: SampleKeywordPreset[] = [
  { keyword: "mineral sunscreen spf 50", searchVolume: 8420, heroRank: 3, relevance: 92, ppcBid: 2.85, asinDensity: 78, keywordSales: 4200, searchTrend: 185, competitors: 8000 },
  { keyword: "reef safe sunscreen", searchVolume: 14200, heroRank: 12, relevance: 78, ppcBid: 1.90, asinDensity: 62, keywordSales: 2650, searchTrend: 120, competitors: 12500 },
  { keyword: "sunscreen for sensitive skin", searchVolume: 22100, heroRank: 28, relevance: 65, ppcBid: 3.10, asinDensity: 45, keywordSales: 890, searchTrend: 40, competitors: 34000 },
  { keyword: "tinted moisturizer spf", searchVolume: 6800, heroRank: 5, relevance: 88, ppcBid: 2.40, asinDensity: 85, keywordSales: 6100, searchTrend: 210, competitors: 2800 },
];
